import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnsavedChangesContext } from '@/contexts/UnsavedChangesContext';
import { ConfirmModal } from '@/components/ui/confirm-modal';

type LeaveStep = 'confirm_leave' | 'save_choice';

/**
 * Two-step leave prompt. Step one asks whether to leave at all; on pages that registered a
 * save-draft handler, step two then asks whether to save the changes as a draft first.
 */
export function GlobalUnsavedChangesModal() {
  const navigate = useNavigate();
  const { pendingNavigation, confirmNavigation, cancelNavigation, setHasUnsavedChanges, unsavedMessage, getSaveDraftHandler } = useUnsavedChangesContext();
  const [leave_step, setLeaveStep] = useState<LeaveStep>('confirm_leave');
  const [is_saving_draft, setIsSavingDraft] = useState(false);

  const resetStep = () => {
    setLeaveStep('confirm_leave');
    setIsSavingDraft(false);
  };

  const leavePage = () => {
    if (pendingNavigation) {
      const destination = pendingNavigation;
      setHasUnsavedChanges(false);
      confirmNavigation();
      resetStep();
      navigate(destination);
    }
  };

  const handleStay = () => {
    resetStep();
    cancelNavigation();
  };

  const handleLeave = () => {
    if(getSaveDraftHandler()){
      setLeaveStep('save_choice');
      return;
    }
    leavePage();
  };

  const handleSaveDraft = async () => {
    const save_draft = getSaveDraftHandler();
    if(!save_draft || !pendingNavigation){
      leavePage();
      return;
    }

    setIsSavingDraft(true);
    let is_saved = false;
    try{
      is_saved = await save_draft(pendingNavigation);
    }
    catch{
      is_saved = false;
    }

    if(is_saved){
      leavePage();
      return;
    }

    /* The page shows why the save did not happen (validation, error, or a follow-up prompt). */
    handleStay();
  };

  if(leave_step === 'save_choice'){
    return (
      <ConfirmModal
        isOpen={!!pendingNavigation}
        title="Save your changes?"
        message="Save your changes as a draft so you can pick up where you left off, or leave without saving and lose them."
        confirmText={is_saving_draft ? 'Saving...' : 'Save'}
        cancelText="Don't Save"
        confirmPosition="right"
        confirmVariant="primary"
        isBusy={is_saving_draft}
        onConfirm={handleSaveDraft}
        onCancel={leavePage}
        onDismiss={handleStay}
      />
    );
  }

  return (
    <ConfirmModal
      isOpen={!!pendingNavigation}
      title="Leave this page?"
      message={unsavedMessage ?? 'Are you sure you want to leave this page?'}
      confirmText="Leave"
      cancelText="Stay"
      onConfirm={handleLeave}
      onCancel={handleStay}
    />
  );
}
